import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Member } from '@/features/backlog/types';
import { MENTION_RE } from '@/features/backlog/mentions';
import { cn } from '@/components/ui/cn';
import MemberAvatar from '@/components/member-avatar';

/**
 * A comment editor with @-mention autocomplete that renders each picked member as an inline,
 * non-editable CHIP (contenteditable). Type "@" then a name for the dropdown (arrows / Enter / Tab
 * to pick). The value is serialized to encoded tokens (@[Name](id)) for storage; the backend parses
 * the ids to notify, and the display renders them as highlighted names. ⌘/Ctrl-Enter submits.
 */
export default function MentionInput({
  value,
  onChange,
  members,
  placeholder,
  onSubmit,
  size = 'md',
}: {
  value: string;
  onChange: (v: string) => void;
  members: Member[];
  placeholder?: string;
  onSubmit?: () => void;
  /** 'lg' bumps the editor + autocomplete ~33% (used by the larger whiteboard comment composer). */
  size?: 'md' | 'lg';
}) {
  const lg = size === 'lg';
  const ref = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const lastValue = useRef<string>(' '); // force an initial build
  const [menu, setMenu] = useState<{ query: string } | null>(null);
  const [sel, setSel] = useState(0);
  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  const chipEl = (id: string, name: string) => {
    const s = document.createElement('span');
    s.contentEditable = 'false';
    s.dataset.id = id;
    s.dataset.name = name;
    s.className = 'mention-chip';
    s.textContent = `@${name}`;
    return s;
  };

  // Build the editor DOM from a stored value string (text + mention chips).
  const build = (v: string) => {
    const el = ref.current;
    if (!el) return;
    el.textContent = '';
    let last = 0;
    let m: RegExpExecArray | null;
    const re = new RegExp(MENTION_RE);
    const appendText = (t: string) => t && el.appendChild(document.createTextNode(t));
    while ((m = re.exec(v)) !== null) {
      appendText(v.slice(last, m.index));
      el.appendChild(chipEl(m[2], byId.get(m[2])?.name ?? m[1]));
      last = m.index + m[0].length;
    }
    appendText(v.slice(last));
    lastValue.current = v;
  };

  // Serialize the editor DOM back to the stored value.
  const serialize = (): string => {
    const el = ref.current;
    if (!el) return '';
    let out = '';
    const walk = (node: Node) => {
      node.childNodes.forEach((n) => {
        if (n.nodeType === Node.TEXT_NODE) out += n.textContent;
        else if (n instanceof HTMLElement && n.dataset.id) out += `@[${n.dataset.name}](${n.dataset.id})`;
        else if (n instanceof HTMLElement && (n.tagName === 'BR')) out += '\n';
        else if (n instanceof HTMLElement && (n.tagName === 'DIV' || n.tagName === 'P')) {
          out += '\n';
          walk(n);
        } else walk(n);
      });
    };
    walk(el);
    return out.replace(/\n+$/,'');
  };

  // Reflect external value changes (edit-init, cleared after submit) — but not our own edits, which
  // would fight the caret.
  useEffect(() => {
    if (value !== lastValue.current) build(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const matches = menu
    ? members.filter((m) => m.active && `${m.name} ${m.email ?? ''}`.toLowerCase().includes(menu.query.toLowerCase())).slice(0, 6)
    : [];

  const emit = () => {
    const v = serialize();
    lastValue.current = v;
    onChange(v);
  };

  // Reset the highlighted candidate to the top ONLY when the query actually changes — not on every
  // caret/keyup sync. Otherwise the keyUP after an ArrowDown re-ran syncMenu and snapped the highlight
  // back to 0, so arrowing down appeared to bounce back to the first member.
  const lastQuery = useRef<string | null>(null);

  // Look at the text just before the caret to decide whether an @-query menu should show.
  const syncMenu = () => {
    const s = window.getSelection();
    const close = () => {
      setMenu(null);
      lastQuery.current = null;
    };
    if (!s || s.rangeCount === 0 || !ref.current?.contains(s.anchorNode)) return close();
    const node = s.anchorNode;
    if (!node || node.nodeType !== Node.TEXT_NODE) return close();
    const before = (node.textContent ?? '').slice(0, s.anchorOffset);
    const m = /(^|\s)@([\p{L}\p{N}._-]*)$/u.exec(before);
    const nextQuery = m ? m[2] : null;
    if (nextQuery === null) return close();
    if (nextQuery !== lastQuery.current) {
      setMenu({ query: nextQuery });
      setSel(0); // new candidate list → start at the top
      lastQuery.current = nextQuery;
    }
  };

  const insert = (mem: Member) => {
    const s = window.getSelection();
    if (!s || s.rangeCount === 0) return;
    const range = s.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return;
    const text = node.textContent ?? '';
    const caret = range.startOffset;
    const before = text.slice(0, caret);
    const m = /(^|\s)@([\p{L}\p{N}._-]*)$/u.exec(before);
    if (!m) return;
    const atStart = m.index + m[1].length; // position of the '@'
    // Rewrite: [text up to '@'] + chip + ' ' + [rest]
    const pre = text.slice(0, atStart);
    const post = text.slice(caret);
    const parent = node.parentNode!;
    const chip = chipEl(mem.id, mem.name);
    const space = document.createTextNode(' ');
    const preNode = document.createTextNode(pre);
    const postNode = document.createTextNode(post);
    parent.replaceChild(postNode, node);
    parent.insertBefore(space, postNode);
    parent.insertBefore(chip, space);
    parent.insertBefore(preNode, chip);
    // caret right after the trailing space
    const r = document.createRange();
    r.setStart(space, 1);
    r.collapse(true);
    s.removeAllRanges();
    s.addRange(r);
    setMenu(null);
    emit();
  };

  const empty = value.trim() === '';

  // Position the @-mention dropdown in a PORTAL (fixed, at document.body) so it escapes any
  // overflow-hidden / clipping ancestor — e.g. the whiteboard comment popover, where it was clipped
  // and invisible. Flip it ABOVE the input when there isn't room below (composer at a panel's bottom).
  const rect = wrapRef.current?.getBoundingClientRect();
  const MENU_MAXH = lg ? 320 : 260;
  const openUp = !!rect && window.innerHeight - rect.bottom < MENU_MAXH + 12 && rect.top > window.innerHeight - rect.bottom;
  const menuStyle: CSSProperties = rect
    ? {
        position: 'fixed',
        left: rect.left,
        width: Math.max(lg ? 260 : 220, Math.min(lg ? 340 : 280, rect.width)),
        ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      }
    : { display: 'none' };

  return (
    // min-w-0 lets this flex child shrink to its allotted width instead of growing to its content's
    // intrinsic width (which pushed the neighbouring submit button out of the fixed-width composer).
    <div ref={wrapRef} className="relative min-w-0 flex-1">
      <div
        ref={ref}
        role="textbox"
        aria-multiline="true"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder ?? 'Write a comment… (@ to mention)'}
        className={cn(
          // break-words wraps a long unbroken word/URL instead of forcing horizontal overflow.
          'mention-editor w-full whitespace-pre-wrap break-words rounded-lg border border-line bg-surface-inset text-fg outline-none transition-colors focus:border-line-strong',
          lg ? 'min-h-[52px] px-4 py-2.5 text-[16px]' : 'min-h-[40px] px-3 py-2 text-[13px]',
        )}
        onInput={() => {
          emit();
          syncMenu();
        }}
        onKeyUp={syncMenu}
        onMouseUp={syncMenu}
        onKeyDown={(e) => {
          if (menu && matches.length) {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSel((x) => (x + 1) % matches.length);
              return;
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSel((x) => (x - 1 + matches.length) % matches.length);
              return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
              e.preventDefault();
              insert(matches[sel]);
              return;
            }
            if (e.key === 'Escape') {
              // Dismiss just the autocomplete — stop the event so an ancestor Escape handler (e.g. the
              // comment thread popover) doesn't also close on the same keypress.
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setMenu(null);
              return;
            }
          }
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSubmit?.();
          }
        }}
        onBlur={() => setTimeout(() => setMenu(null), 150)}
      />
      {empty ? null : null}
      {menu && matches.length
        ? createPortal(
            <div
              // z-[2150] floats the autocomplete above every surface that can host this composer — the
              // details MODAL (z-[2000]) and the notification-bell Popover (z-[2100]) included — while
              // staying under transient top layers (history toast z-[2200], lightbox z-[3000]). At z-[70]
              // it rendered BEHIND the modal drawer (a body-portal sibling at z-[2000]) and never showed.
              style={menuStyle}
              className={cn('z-[2150] overflow-y-auto rounded-xl border border-line bg-surface-raised p-1 shadow-2xl shadow-black/50', lg ? 'max-h-[320px]' : 'max-h-[260px]')}
            >
              {matches.map((m, i) => (
                <button
                  key={m.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insert(m);
                  }}
                  className={cn('flex w-full items-center rounded-lg text-left transition-colors', lg ? 'gap-2.5 px-2.5 py-2' : 'gap-2 px-2 py-1.5', i === sel ? 'bg-layer' : 'hover:bg-layer')}
                >
                  <MemberAvatar member={m} size={lg ? 28 : 22} />
                  <div className="min-w-0">
                    <div className={cn('truncate text-fg', lg ? 'text-[16px]' : 'text-[13px]')}>{m.name}</div>
                    {m.email ? <div className={cn('truncate text-fg-faint', lg ? 'text-[13px]' : 'text-[11px]')}>{m.email}</div> : null}
                  </div>
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
